import { db } from '../firebase';
import {
  collection,
  doc,
  getDocs,
  addDoc,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy
} from 'firebase/firestore';

/**
 * Service for managing custom character sets in Firestore
 * Stores sets under workspace/{companyId}/customCharacterSets collection
 *
 * Character set structure:
 * - name: string (user-provided display name)
 * - playerAnimationFrames: string[] (Firebase Storage URLs)
 * - playerJumpImage: string | null (optional jump/flap frame URL)
 * - playerImage: string | null (optional static image for verticalclimber)
 * - createdAt: timestamp
 */

const MAX_CHARACTER_SETS = 20;

class CustomCharacterSetsService {

  getCollection(companyId) {
    return collection(db, 'workspace', companyId, 'customCharacterSets');
  }

  async getCompanyCharacterSets(companyId) {
    try {
      const ref = this.getCollection(companyId);
      const q = query(ref, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const sets = [];
      snapshot.forEach(doc => {
        sets.push({ id: doc.id, ...doc.data() });
      });
      return sets;
    } catch (error) {
      console.error('Error fetching custom character sets:', error);
      return [];
    }
  }

  async saveCharacterSet(companyId, data) {
    if (!data.name?.trim()) {
      return { error: 'invalid', message: 'Name is required.' };
    }
    if (!data.playerAnimationFrames?.length) {
      return { error: 'invalid', message: 'At least one animation frame is required.' };
    }

    const existing = await this.getCompanyCharacterSets(companyId);
    if (existing.length >= MAX_CHARACTER_SETS) {
      return { error: 'limit_reached', message: `Maximum ${MAX_CHARACTER_SETS} character sets. Delete some to save more.` };
    }

    try {
      const ref = this.getCollection(companyId);
      const newSet = {
        name: data.name.trim(),
        playerAnimationFrames: data.playerAnimationFrames,
        playerJumpImage: data.playerJumpImage || null,
        playerImage: data.playerImage || null,
        createdAt: serverTimestamp(),
      };
      const docRef = await addDoc(ref, newSet);
      return { id: docRef.id, ...newSet };
    } catch (error) {
      console.error('Error saving character set:', error);
      throw error;
    }
  }

  async deleteCharacterSet(companyId, setId) {
    try {
      const ref = doc(db, 'workspace', companyId, 'customCharacterSets', setId);
      await deleteDoc(ref);
      return true;
    } catch (error) {
      console.error('Error deleting character set:', error);
      throw error;
    }
  }

  getMaxCharacterSets() {
    return MAX_CHARACTER_SETS;
  }
}

const customCharacterSetsService = new CustomCharacterSetsService();
export default customCharacterSetsService;
